import "../assets/styles/pages/skills.css";
import React, { useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";

import logoJava from "../assets/images/skills/java.svg";
import logoSpringBoot from "../assets/images/skills/spring-boot.svg";
import logoSpringSecurity from "../assets/images/skills/spring-security.svg";
import logoSpringCloud from "../assets/images/skills/spring-cloud.svg";
import logoSpringData from "../assets/images/skills/spring-data.svg";
import logoHibernate from "../assets/images/skills/hibernate.svg";
import logoFlyway from "../assets/images/skills/flyway.png";
import logoJUnit from "../assets/images/skills/junit.png";
import logoOpenAPI from "../assets/images/skills/openapi.svg";
import logoRestAPI from "../assets/images/skills/rest-api.svg";
import logoGraphQL from "../assets/images/skills/graphql.svg";
import logoOracle from "../assets/images/skills/oracle.png";
import logoMongoDB from "../assets/images/skills/mongodb.svg";
import logoPostgreSQL from "../assets/images/skills/elephant.png";
import logoKafka from "../assets/images/skills/kafka.png";
import logoGit from "../assets/images/skills/git.svg";
import logoHTML from "../assets/images/skills/HTML.svg";
import logoCSS from "../assets/images/skills/CSS.svg";
import logoReact from "../assets/images/skills/react.svg";
import logoJavaScript from "../assets/images/skills/js.svg";
import logoTypeScript from "../assets/images/skills/typescript.svg";
import logoFigma from "../assets/images/skills/figma.svg";
import logoDocker from "../assets/images/skills/docker.png";
import logoAWS from "../assets/images/skills/aws.svg";
import logoRedis from "../assets/images/skills/redis.png";
import logoNginx from "../assets/images/skills/nginx.svg";
import logoGrpc from "../assets/images/skills/grpc.svg";
import logoElk from "../assets/images/skills/elk.svg";
import logoGrafana from "../assets/images/skills/grafana.svg";
import logoLiquibase from "../assets/images/skills/liquibase.png";

type LogoSize = "l" | "m" | "s";

type Skill = { name: string; src: string; alt: string; size: LogoSize; lightPad?: boolean };

const SKILL_GROUPS: { title: string; items: Skill[] }[] = [
    {
        title: "Backend & Java",
        items: [
            { name: "Java", src: logoJava, alt: "Java", size: "l" },
            { name: "Spring Boot", src: logoSpringBoot, alt: "Spring Boot", size: "m" },
            { name: "Spring Security", src: logoSpringSecurity, alt: "Spring Security", size: "m" },
            { name: "Spring Cloud", src: logoSpringCloud, alt: "Spring Cloud", size: "s" },
            { name: "Spring Data", src: logoSpringData, alt: "Spring Data", size: "m" },
            { name: "Hibernate", src: logoHibernate, alt: "Hibernate", size: "m" },
            { name: "Flyway", src: logoFlyway, alt: "Flyway", size: "m" },
            { name: "Liquibase", src: logoLiquibase, alt: "Liquibase", size: "m" },
            { name: "JUnit", src: logoJUnit, alt: "JUnit", size: "m" },
        ],
    },
    {
        title: "APIs",
        items: [
            { name: "OpenAPI", src: logoOpenAPI, alt: "OpenAPI", size: "l" },
            { name: "REST", src: logoRestAPI, alt: "REST", size: "m", lightPad: true },
            { name: "GraphQL", src: logoGraphQL, alt: "GraphQL", size: "m" },
            { name: "gRPC", src: logoGrpc, alt: "gRPC", size: "m" },
        ],
    },
    {
        title: "Data & messaging",
        items: [
            { name: "Oracle", src: logoOracle, alt: "Oracle", size: "s" },
            { name: "MongoDB", src: logoMongoDB, alt: "MongoDB", size: "m" },
            { name: "PostgreSQL", src: logoPostgreSQL, alt: "PostgreSQL", size: "s" },
            { name: "Redis", src: logoRedis, alt: "Redis", size: "m" },
            { name: "Kafka", src: logoKafka, alt: "Kafka", size: "m", lightPad: true },
        ],
    },
    {
        title: "Cloud & tools",
        items: [
            { name: "Docker", src: logoDocker, alt: "Docker", size: "s" },
            { name: "NGINX", src: logoNginx, alt: "NGINX", size: "m" },
            { name: "AWS", src: logoAWS, alt: "AWS", size: "m" },
            { name: "ELK", src: logoElk, alt: "ELK Stack", size: "m" },
            { name: "Grafana", src: logoGrafana, alt: "Grafana", size: "m" },
            { name: "Git", src: logoGit, alt: "Git", size: "m" },
            { name: "Figma", src: logoFigma, alt: "Figma", size: "m" },
        ],
    },
    {
        title: "Frontend",
        items: [
            { name: "HTML", src: logoHTML, alt: "HTML", size: "m" },
            { name: "CSS", src: logoCSS, alt: "CSS", size: "m" },
            { name: "React", src: logoReact, alt: "React", size: "m" },
            { name: "JavaScript", src: logoJavaScript, alt: "JavaScript", size: "m" },
            { name: "TypeScript", src: logoTypeScript, alt: "TypeScript", size: "m" },
        ],
    },
];

const FLAT_SKILLS: Skill[] = SKILL_GROUPS.flatMap((g) => g.items);

const MOBILE_MQ = "(max-width: 900px)";

function useSkillsMobileLayout(): boolean {
    const [mobile, setMobile] = useState(() =>
        typeof window !== "undefined" ? window.matchMedia(MOBILE_MQ).matches : false
    );

    useEffect(() => {
        const mq = window.matchMedia(MOBILE_MQ);
        const apply = () => setMobile(mq.matches);
        apply();
        mq.addEventListener("change", apply);
        return () => mq.removeEventListener("change", apply);
    }, []);

    return mobile;
}

function SkillLogo({ s }: { s: Skill }) {
    const img = (
        <img
            className={`skill-chip__logo skill-chip__logo--${s.size}`}
            src={s.src}
            alt=""
            loading="lazy"
            decoding="async"
        />
    );

    if (s.lightPad) {
        return <span className={`skill-chip__logo-pad skill-chip__logo-pad--${s.size}`}>{img}</span>;
    }

    return img;
}

function SkillChip({ s, stagger }: { s: Skill; stagger?: number }) {
    return (
        <div
            className="skill-chip"
            style={stagger !== undefined ? ({ "--stagger": stagger } as React.CSSProperties) : undefined}
        >
            <SkillLogo s={s} />
            <span className="skill-chip__label">{s.name}</span>
        </div>
    );
}

function Skills() {
    const mobile = useSkillsMobileLayout();
    const [ref, inView] = useInView({ threshold: 0.08, triggerOnce: true, rootMargin: "0px 0px -5% 0px" });

    return (
        <section id="skills" className="skills section section--tight bg-raised" aria-labelledby="skills-title">
            <div className="shell">
                <header className="section__header">
                    <p className="section__eyebrow">Toolkit</p>
                    <h2 id="skills-title" className="section__title">
                        Skills
                    </h2>
                    <p className="section__lead">
                        Technologies I use regularly across backend services, data stores, APIs, and frontend delivery.
                    </p>
                </header>

                <div ref={ref} className={`skills-presentation${inView ? " skills-presentation--visible" : ""}`}>
                    {!mobile ? (
                        <div className="skills-grid">
                            {FLAT_SKILLS.map((s, i) => (
                                <SkillChip key={s.name} s={s} stagger={i} />
                            ))}
                        </div>
                    ) : (
                        <div className="skills-accordion">
                            {SKILL_GROUPS.map((group) => (
                                <details key={group.title} className="skills-accordion__item" name="portfolio-skills">
                                    <summary className="skills-accordion__summary">{group.title}</summary>
                                    <div className="skills-accordion__panel">
                                        <div className="skills-accordion__chips">
                                            {group.items.map((s) => (
                                                <SkillChip key={s.name} s={s} />
                                            ))}
                                        </div>
                                    </div>
                                </details>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

export default Skills;
